import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { camelCase } from 'lodash-es';

import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { CloseIcon } from '@udacity/chakra-uds-icons';

import { getSkillLocaleLabelMapping, transformRawDurationFilter } from '~/features/catalog/catalog-filter-data';
import { RAW_DURATION_KEYS, RAW_DURATION_REVERSE_MAPPING } from '~/features/catalog/constants';
import { useCatalogFilterLocaleMetadata } from '~/features/catalog/hooks/use-catalog-filter-locale-metadata';
import { Filters } from '~/features/catalog/models/filter';
import { updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { FilterControlsIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useI18n } from '~/hooks/use-i18n';

import { clearCatalogSearchFilters } from '../utils/clear-catalog-search-filters';

const SIMPLE_FILTERS = ['price', 'difficulty', 'scope', 'school'] as const;
const TAG_FILTER_TYPES = ['price', 'availability', 'school', 'skill', 'difficulty', 'duration', 'type'] as const;

type ActiveFilterTag = {
  filterType: keyof Filters;
  label: string;
  value: string;
};

export function CatalogSearchActiveFilters({ selectedFilters }: { selectedFilters: Filters }) {
  const router = useRouter();
  const { t, i18n } = useI18n();
  const { catalogFilterLocaleMetadata } = useCatalogFilterLocaleMetadata(i18n.language);
  const skillLocaleLabelMapping = getSkillLocaleLabelMapping(catalogFilterLocaleMetadata?.skills ?? []);

  const specialMappings: Partial<Record<keyof Filters, Record<string, string>>> = useMemo(
    () => ({
      type: {
        course: t('program.semanticType.course'),
        'nanodegree program': t('program.semanticType.degree'),
      },
      availability: {
        'Enroll Now': t('catalog.filters.option.enrollNow'),
        'Request Access': t('catalog.filters.option.requestAccess'),
      },
      rawDuration: {
        [RAW_DURATION_KEYS.LT20]: t('catalog.filters.option.raw.lessThan20'),
        [RAW_DURATION_KEYS.MID]: t('catalog.filters.option.raw.between20And60'),
        [RAW_DURATION_KEYS.GT60]: t('catalog.filters.option.raw.greaterThan60'),
      },
      skill: skillLocaleLabelMapping,
    }),
    [t, skillLocaleLabelMapping]
  );

  function getFilterDisplayText(filterType: keyof Filters, filterValue: string) {
    if ((SIMPLE_FILTERS as readonly string[]).includes(filterType)) {
      return t(`catalog.filters.option.${camelCase(filterValue)}`);
    }
    return specialMappings[filterType]?.[filterValue] || filterValue;
  }

  const tags: ActiveFilterTag[] = [
    ...TAG_FILTER_TYPES.flatMap((filterType) =>
      (selectedFilters[filterType] ?? []).map((value) => ({
        filterType,
        value,
        label: filterType === 'duration' ? value : getFilterDisplayText(filterType, value),
      }))
    ),
    ...(
      transformRawDurationFilter(selectedFilters.rawDuration ? [...selectedFilters.rawDuration] : undefined) ?? []
    ).map((label) => ({
      filterType: 'rawDuration' as const,
      value: RAW_DURATION_REVERSE_MAPPING[label],
      label: getFilterDisplayText('rawDuration', label),
    })),
  ];

  if (!tags.length) {
    return null;
  }

  function handleRemove({ filterType, value }: ActiveFilterTag) {
    const remainingValues = selectedFilters[filterType]?.filter((selectedValue) => selectedValue !== value);

    updateCatalogUrlState({
      router,
      updatedRouteParameters: {
        page: undefined,
        [filterType]: remainingValues?.join(',') || undefined,
      },
    });
  }

  function renderClearAll() {
    return (
      <Button
        color='blue.500'
        flexShrink={0}
        fontSize='0.75rem'
        fontWeight='500'
        lineHeight='1'
        paddingX='4px'
        variant='link'
        onClick={() => clearCatalogSearchFilters(router)}
      >
        {t('catalog.filters.clearAll')}
      </Button>
    );
  }

  return (
    <Flex alignItems={{ md: 'center' }} flexDirection={{ base: 'column', md: 'row' }} gap='10px'>
      <Flex
        alignItems='center'
        flexShrink={0}
        gap='10px'
        justifyContent='space-between'
        width={{ base: '100%', md: 'auto' }}
      >
        <Flex alignItems='center' color='grey.500' fontSize='0.8125rem' gap='6px' lineHeight='1.4'>
          <FilterControlsIcon height='16px' width='16px' />
          {t('catalog.filters.activeFilters')}
        </Flex>
        <Box display={{ base: 'block', md: 'none' }}>{renderClearAll()}</Box>
      </Flex>
      <Flex alignItems='center' flex={1} flexWrap='wrap' gap='8px'>
        {tags.map((tag) => (
          <Flex
            key={`${tag.filterType}-${tag.value}`}
            alignItems='center'
            aria-label={t('catalog.removeFilter', { filter: tag.label }) ?? undefined}
            as='button'
            bgColor='blue.100'
            border='1px solid'
            borderColor='blue.300'
            borderRadius='2px'
            color='blue.700'
            gap='4px'
            padding='6px 6px 6px 8px'
            type='button'
            onClick={() => handleRemove(tag)}
          >
            <Text fontSize='0.75rem' lineHeight='1' textTransform='capitalize' whiteSpace='nowrap'>
              {tag.label}
            </Text>
            <CloseIcon h='12px' w='12px' />
          </Flex>
        ))}
      </Flex>
      <Box display={{ base: 'none', md: 'block' }}>{renderClearAll()}</Box>
    </Flex>
  );
}
